import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, X, Loader2, Tag, Check } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * POCategoriesManager — searchable list of PO categories with inline rename
 * and one-click retire. Categories are what accountants pick from the
 * mandatory PO category dropdown; renaming here cascades onto every past PO
 * (server-side) so analytics stay coherent.
 *
 * Retire is a super-admin only action (soft-delete) enforced by the backend.
 */
export default function POCategoriesManager({ api, canDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/accounting/po-categories');
      setRows(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); }, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter(v => (v.name || '').toLowerCase().includes(n));
  }, [rows, q]);

  const createCategory = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Category name is required'); return; }
    setSaving(true);
    try {
      await api.post('/api/admin/accounting/po-categories', { name });
      toast.success(`Category "${name}" added`);
      setNewName('');
      setCreating(false);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add category');
    } finally { setSaving(false); }
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setEditingName(row.name || '');
  };

  const saveEdit = async (row) => {
    const name = editingName.trim();
    if (!name) { toast.error('Category name is required'); return; }
    if (name === row.name) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/api/admin/accounting/po-categories/${row._id}`, { name });
      toast.success('Category renamed');
      setEditingId(null);
      setEditingName('');
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally { setSaving(false); }
  };

  const retireCategory = async (row) => {
    if (!window.confirm(`Retire "${row.name}"?\n\nExisting POs keep their label, but the picker will no longer offer this option.`)) return;
    try {
      await api.delete(`/api/admin/accounting/po-categories/${row._id}`);
      toast.success('Category retired');
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retire failed');
    }
  };

  return (
    <div data-testid="po-categories-manager">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e]">PO Categories</h2>
          <p className="text-xs text-neutral-500">Rename or retire the tags every purchase order must pick from.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories…" className="pl-10 w-64" data-testid="po-category-search" />
          </div>
          <Button onClick={() => setCreating(true)} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="po-category-add">
            <Plus className="w-4 h-4 mr-2" /> Add Category
          </Button>
        </div>
      </div>

      {creating && (
        <Card className="mb-3 p-3 flex items-center gap-2 border-fuchsia-200 bg-fuchsia-50/50" data-testid="po-category-create-row">
          <Tag className="w-4 h-4 text-fuchsia-500 shrink-0" />
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createCategory(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
            placeholder="New category name (e.g. Print Ads, Cloud Ops)"
            className="flex-1"
            data-testid="po-category-new-input"
          />
          <Button onClick={createCategory} disabled={saving} size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="po-category-new-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
          </Button>
          <Button onClick={() => { setCreating(false); setNewName(''); }} size="sm" variant="outline">Cancel</Button>
        </Card>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-neutral-500">
            <Tag className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="text-sm">{q ? 'No categories match your search.' : 'No categories yet — add your first one.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold w-40">Added by</th>
                  <th className="px-4 py-3 text-right font-semibold w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filtered.map(row => {
                  const isEditing = editingId === row._id;
                  return (
                    <tr key={row._id} className="hover:bg-neutral-50" data-testid={`po-category-row-${row._id}`}>
                      <td className="px-4 py-3 font-medium text-neutral-900">
                        {isEditing ? (
                          <Input
                            autoFocus
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(row); if (e.key === 'Escape') { setEditingId(null); setEditingName(''); } }}
                            className="max-w-xs"
                            data-testid={`po-category-edit-input-${row._id}`}
                          />
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <Tag className="w-3.5 h-3.5 text-fuchsia-500" />
                            {row.name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-neutral-500 text-xs">
                        {row.created_by === 'system' ? <span className="italic">Auto-seeded</span> : <span className="font-mono">{row.created_by?.slice(-6) || '—'}</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(row)} disabled={saving} title="Save" className="p-1.5 hover:bg-emerald-100 rounded-lg text-emerald-700 disabled:opacity-50" data-testid={`po-category-save-${row._id}`}>
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              </button>
                              <button onClick={() => { setEditingId(null); setEditingName(''); }} title="Cancel" className="p-1.5 hover:bg-neutral-100 rounded-lg text-neutral-500">
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEdit(row)} title="Rename" className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-700" data-testid={`po-category-edit-${row._id}`}>
                                <Pencil className="w-4 h-4" />
                              </button>
                              {canDelete && (
                                <button onClick={() => retireCategory(row)} title="Retire" className="p-1.5 hover:bg-red-100 rounded-lg text-red-600" data-testid={`po-category-retire-${row._id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {!canDelete && (
        <p className="text-[11px] text-neutral-500 mt-2">Only super-admins can retire a category. Ask them if a tag needs to go.</p>
      )}
    </div>
  );
}
